import classNames from 'classnames';
import React from 'react';

import {CalendarFile} from 'calendar/CalendarConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import HelpTooltip from 'help/HelpTooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {apiIdToIconName} from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';

import CalendarUtil from './CalendarUtil';

interface AccessedFileProps {
  compact: boolean;
  file: CalendarFile;
}

export class AccessedFile extends React.Component<AccessedFileProps> {
  openFile(): void {
    FileBrowserActions.openFile(this.props.file.id);
  }

  render(): JSX.Element {
    const icon = (
      <BenchlingIcon
        className={classNames('dailyFileActivity-icon', {
          'dailyFileActivity-icon--compact': this.props.compact,
        })}
        icon={
          (this.props.file.tagSchema && this.props.file.tagSchema.iconId) ||
          apiIdToIconName(this.props.file.id)
        }
      />
    );
    return (
      <div
        className={classNames('accessedFile', {
          'accessedFile--compact': this.props.compact,
        })}
      >
        {this.props.file.deleted ? (
          <div>
            {icon}
            <span
              className={classNames('dailyFileActivity-file dailyFileActivity-file--deleted', {
                'legacy-font-proxima-11': this.props.compact,
              })}
            >
              {this.props.file.name}
            </span>
          </div>
        ) : (
          <a
            className={classNames('dailyFileActivity-file', {
              'legacy-font-proxima-11': this.props.compact,
            })}
            onClick={() => {
              this.openFile();
            }}
          >
            {icon}
            {this.props.file.name}
          </a>
        )}
        {!this.props.compact && (
          <div className="dailyFileActivity-accessTime">
            {this.props.file.wasModified ? 'Modified ' : 'Viewed '}
            {DateUtil.readableTime(this.props.file.endAccess, {
              showYear: false,
              showDate: false,
              showTime: true,
            })}
          </div>
        )}
      </div>
    );
  }
}

interface DailyFileActivityProps {
  className?: string;
  compact: boolean;
  files: Array<CalendarFile>;
  // DailyFileActivity supports being used as a table cell - it needs to render the entire TD (instead of
  // just being nested inside of one) for spacing issues.
  renderAsTD?: boolean;
}

export default class DailyFileActivity extends React.Component<DailyFileActivityProps> {
  modifiedFiles(): Array<CalendarFile> {
    return this.props.files.filter((file) => file.wasModified);
  }

  readOnlyFiles(): Array<CalendarFile> {
    return this.props.files.filter((file) => !file.wasModified);
  }

  renderInnerContent(modifiedFiles: Array<CalendarFile>, readOnlyFiles: Array<CalendarFile>): JSX.Element {
    return (
      <div className="dailyFileActivity-inner">
        {modifiedFiles.length > 0 && (
          <div className={classNames({'u-small-spacing-bottom': this.props.compact})}>
            {this.props.compact && (
              <div className="dailyFileActivity-header">
                Modified
                <HelpTooltip className="u-mini-spacing-left" text="Files you updated or deleted" />
              </div>
            )}
            {modifiedFiles.map((file) => (
              <AccessedFile key={file.id} file={file} compact={this.props.compact} />
            ))}
          </div>
        )}
        {readOnlyFiles.length > 0 && (
          <div>
            {this.props.compact && (
              <div
                className={classNames('dailyFileActivity-header', {
                  'u-tiny-spacing-top': modifiedFiles.length > 0,
                })}
              >
                Viewed
                <HelpTooltip className="u-mini-spacing-left" text="Files you viewed" />
              </div>
            )}
            {readOnlyFiles.map((file) => (
              <AccessedFile key={file.id} file={file} compact={this.props.compact} />
            ))}
          </div>
        )}
      </div>
    );
  }

  render(): JSX.Element {
    const modifiedFiles = CalendarUtil.sortFiles(this.modifiedFiles());
    const readOnlyFiles = CalendarUtil.sortFiles(this.readOnlyFiles());

    const className = classNames(this.props.className, 'dailyFileActivity', {
      'dailyFileActivity--compact': this.props.compact,
    });
    if (this.props.renderAsTD) {
      return <td className={className}>{this.renderInnerContent(modifiedFiles, readOnlyFiles)}</td>;
    }
    return <div className={className}>{this.renderInnerContent(modifiedFiles, readOnlyFiles)}</div>;
  }
}
